import { Wrench } from "lucide-react";
import type { MessageNode } from "./utils";
import { extractTextFromContent, isToolCallResultMessage } from "./utils";
import { extractToolCallError } from "@/modules/agents/components/helpers/richActionHelpers";
import { TimelineStepShell } from "./TimelineStepShell";

interface TimelineToolCallStepProps {
  node: MessageNode;
  name: string;
  args: Record<string, unknown>;
  isExpanded: boolean;
  onToggle: () => void;
  isThreadActive: boolean;
}

/**
 * A plain tool call as a timeline step: wrench row with the tool name, and a
 * collapsible body showing the raw arguments and result. A call that returned
 * an error is marked quietly — a retried lookup is routine, not an alarm.
 */
export function TimelineToolCallStep({
  node,
  name,
  args,
  isExpanded,
  onToggle,
  isThreadActive,
}: TimelineToolCallStepProps) {
  const resultMsg = node.toolResult?.message;
  const resultText =
    resultMsg && isToolCallResultMessage(resultMsg)
      ? extractTextFromContent(resultMsg.result)
      : null;

  const statusText = getToolCallStatusText(node);
  const error = extractToolCallError(node.toolResult);

  return (
    <TimelineStepShell
      icon={Wrench}
      iconClassName="text-blue-700 dark:text-blue-300"
      label={name}
      labelClassName="text-blue-700 dark:text-blue-300"
      meta={
        error ? (
          <span className="shrink-0 text-xs text-muted-foreground">
            No result
          </span>
        ) : statusText ? (
          <span className="shrink-0 text-xs text-warning">{statusText}</span>
        ) : undefined
      }
      isInProgress={isThreadActive && !node.toolResult}
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      <div className="space-y-2">
        <div className="rounded-md border bg-muted/40 p-2.5">
          <div className="mb-1 text-xs font-medium text-muted-foreground">
            Arguments
          </div>
          <pre className="overflow-x-auto whitespace-pre-wrap text-xs text-muted-foreground">
            {JSON.stringify(args, null, 2)}
          </pre>
        </div>
        {resultText && (
          <div className="rounded-md border bg-muted/40 p-2.5">
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              Result
            </div>
            <pre className="max-h-48 overflow-y-auto whitespace-pre-wrap text-xs text-muted-foreground">
              {resultText}
            </pre>
          </div>
        )}
      </div>
    </TimelineStepShell>
  );
}

function getToolCallStatusText(node: MessageNode): string | null {
  if (node.message.task?.status === "PENDING_REVIEW") {
    return "Requires review";
  }
  const timerStatus = node.message.waitTimer?.status;
  if (timerStatus === "WAITING") {
    return "Waiting";
  }
  if (timerStatus === "CANCELLED") {
    return "Timer cancelled";
  }
  return null;
}
